// Protected surface: review docs/protected-surfaces/session-pwa-transport.md before changing PWA fallback auth behavior.
/**
 * PWA Authentication Fallback Utility
 * 
 * This utility handles authentication persistence in PWA mode when cookies fail.
 * PWA apps (especially iOS) have stricter cookie policies, so we need fallback strategies.
 */

import { safeGetItem, safeSetItem, safeRemoveItem } from './storageSafe';

const PWA_AUTH_DEBUG = String(process.env.REACT_APP_AUTH_FLOW_DEBUG || '').trim() === '1';
const pwaAuthDebug = (...args) => {
  if (PWA_AUTH_DEBUG) window.console.log(...args);
};

// Check if running in PWA mode
export const isPWAMode = () => {
  return window.navigator.standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches;
};

// PWA-specific authentication storage
export const pwaAuthStorage = {
  // Store authentication data in localStorage as PWA fallback
  storeAuth: (userData, rememberMe) => {
    // 🎯 SMART STORAGE: Only store fallback when it's actually needed

    // Check if we're in a scenario where fallback might be useful
    const isStandalone = window.navigator.standalone === true ||
      window.matchMedia('(display-mode: standalone)').matches;
    const isLocalhost = window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1';

    // Only store fallback if:
    // 1. We're in PWA mode, OR
    // 2. We're on localhost (where cookies are unreliable), OR  
    // 3. User explicitly checked "Remember Me"
    const shouldStoreFallback = isStandalone || isLocalhost || rememberMe;

    if (!shouldStoreFallback) {
      pwaAuthDebug('[PWA Auth] 🚫 Skipping fallback storage (not needed for this scenario):', {
        isStandalone,
        isLocalhost,
        rememberMe,
        reason: 'Cookies should work reliably in this environment'
      });
      return;
    }

    try {
      const authData = {
        user: userData,
        rememberMe: rememberMe,
        timestamp: Date.now(),
        expiresAt: rememberMe ?
          Date.now() + (30 * 24 * 60 * 60 * 1000) : // ✅ FIXED: 30 days for Remember Me (matches backend)
          Date.now() + (15 * 60 * 1000)   // 15 minutes without Remember Me
      };

      safeSetItem('pwa_auth_data', JSON.stringify(authData));
      safeSetItem('pwa_auth_expires', authData.expiresAt.toString());

      pwaAuthDebug('[PWA Auth] 💾 STORING authentication fallback:', {
        reason: shouldStoreFallback ? 'PWA/localhost/RememberMe' : 'Not needed',
        rememberMe,
        userId: userData?.id || userData?._id,
        username: userData?.username,
        expiresAt: new Date(authData.expiresAt).toISOString(),
        duration: rememberMe ? '30 days' : '15 minutes',
        timestamp: new Date(authData.timestamp).toISOString()
      });

      // 🔍 DEBUG: Verify storage was successful
      const storedData = safeGetItem('pwa_auth_data');
      const storedExpires = safeGetItem('pwa_auth_expires');
      pwaAuthDebug('[PWA Auth] 🔍 Storage verification:', {
        dataStored: !!storedData,
        expiresStored: !!storedExpires,
        storedExpiresValue: storedExpires
      });

    } catch (error) {
      console.error('[PWA Auth] ❌ Failed to store auth fallback:', error);
    }
  },

  // Retrieve authentication data from PWA fallback
  getAuth: () => {
    // ✅ FIXED: Always check fallback, regardless of PWA detection
    // Because PWAs often fail detection and cookies are unreliable

    try {
      const authDataStr = safeGetItem('pwa_auth_data');
      const expiresAt = parseInt(safeGetItem('pwa_auth_expires') || '0');

      if (!authDataStr || !expiresAt) return null;

      // Check if expired
      if (Date.now() > expiresAt) {
        pwaAuthDebug('[PWA Auth] Fallback auth expired, clearing');
        pwaAuthStorage.clearAuth();
        return null;
      }

      const authData = JSON.parse(authDataStr);

      // ✅ IMPROVED: Only clear fallback if server-side cookie EXPLICITLY says 0.
      // If the cookie is missing (Safari/iOS cleared it), we KEEP the fallback.
      const serverRememberMeCookie = document.cookie.match(/(?:^| )FS_REMEMBER=([^;]+)/);
      let serverRememberMeValue = null;

      if (serverRememberMeCookie) {
        serverRememberMeValue = serverRememberMeCookie[1] === '1';
        // If server explicitly says rememberMe=false (0), but we have a long-lived fallback, clear it.
        // This happens if the user logs in WITHOUT "Remember Me" on a device that HAD it before.
        if (!serverRememberMeValue && authData.rememberMe) {
          pwaAuthDebug('[PWA Auth] Server explicitly set FS_REMEMBER=0 (no-remember session), clearing long-lived fallback');
          pwaAuthStorage.clearAuth();
          return null;
        }
      }

      pwaAuthDebug('[PWA Auth] Retrieved fallback auth:', {
        rememberMe: authData.rememberMe,
        serverRememberMe: serverRememberMeValue,
        expiresAt: new Date(authData.expiresAt).toISOString()
      });

      return authData;
    } catch (error) {
      console.error('[PWA Auth] Failed to retrieve auth fallback:', error);
      return null;
    }
  },

  // Clear PWA authentication fallback
  clearAuth: () => {
    // ✅ FIXED: Always clear fallback, regardless of PWA detection
    // Because PWAs often fail detection and cookies are unreliable

    safeRemoveItem('pwa_auth_data');
    safeRemoveItem('pwa_auth_expires');
    pwaAuthDebug('[PWA Auth] Cleared authentication fallback');
  },

  // Check if PWA auth fallback is valid
  isAuthValid: () => {
    // ✅ FIXED: Always check validity, regardless of PWA detection
    const authData = pwaAuthStorage.getAuth();
    return authData !== null;
  },

  // Get remaining time for PWA auth
  getRemainingTime: () => {
    const authData = pwaAuthStorage.getAuth();
    if (!authData) return 0;

    const remaining = authData.expiresAt - Date.now();
    return Math.max(0, remaining);
  }
};

// Enhanced authentication check that uses both cookies and PWA fallback
export const checkPWAAuth = async (apiClient) => {
  // ✅ CRITICAL FIX: Check logout flag first
  if (typeof window !== 'undefined' && window.__LOGGED_OUT) {
    pwaAuthDebug('[PWA Auth] User logged out, skipping authentication check');
    return { success: false, error: 'logged_out', method: 'guard' };
  }

  // ✅ Only probe if there's an actual session hint
  const hasRemember = typeof document !== 'undefined' && /(?:^|;\s*)FS_REMEMBER=1(?:;|$)/.test((document.cookie || ''));
  const hasFallback = !!(typeof localStorage !== 'undefined' && localStorage.getItem('pwa_auth_data'));
  const hasAccess = typeof document !== 'undefined' && /(?:^|;\s*)accessToken=/.test((document.cookie || ''));
  if (!hasRemember && !hasFallback && !hasAccess) {
    return { success: false, error: 'no_session_hint', method: 'guard' };
  }

  // 🔍 DEBUG: Log current cookie state
  pwaAuthDebug('[PWA Auth] 🔍 Current cookie state:', {
    hasAccessToken: document.cookie.includes('accessToken='),
    hasRefreshToken: document.cookie.includes('refreshToken='),
    hasFSRemember: document.cookie.includes('FS_REMEMBER='),
    hasDeviceId: document.cookie.includes('deviceId='),
    allCookies: document.cookie || 'No cookies present'
  });

  // 🔍 DEBUG: Log PWA mode detection
  const isStandalone = window.navigator.standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches;
  pwaAuthDebug('[PWA Auth] 🔍 PWA Mode Detection:', {
    navigatorStandalone: window.navigator.standalone,
    mediaQueryMatch: window.matchMedia('(display-mode: standalone)').matches,
    isPWAMode: isStandalone
  });

  // 🔍 DEBUG: Check environment
  const isLocalhost = window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';
  const isHTTPS = window.location.protocol === 'https:';
  pwaAuthDebug('[PWA Auth] 🔍 Environment:', {
    isLocalhost,
    isHTTPS,
    hostname: window.location.hostname,
    protocol: window.location.protocol
  });

  try {
    pwaAuthDebug('[PWA Auth] 🚀 Attempting cookie-based authentication...');
    // First try the normal cookie-based authentication
    const response = await apiClient.get('/users/me');
    pwaAuthDebug('[PWA Auth] ✅ Cookie authentication SUCCESS:', {
      method: 'cookies',
      userId: response.data?.id || response.data?._id,
      username: response.data?.username
    });
    return { success: true, user: response.data, method: 'cookies' };
  } catch (error) {
    pwaAuthDebug('[PWA Auth] ❌ Cookie authentication FAILED:', {
      error: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.config?.url
    });

    // 🎯 SMART FALLBACK: Only use fallback in specific scenarios
    const shouldUseFallback = (
      // PWA mode + cookies failed
      isStandalone &&
      // AND either localhost (where cookies don't work) OR production with fallback available
      (isLocalhost || pwaAuthStorage.getAuth())
    );

    if (shouldUseFallback) {
      pwaAuthDebug('[PWA Auth] 🔄 Using smart fallback (PWA mode + cookies failed)');

      const fallbackAuth = pwaAuthStorage.getAuth();
      if (fallbackAuth) {
        pwaAuthDebug('[PWA Auth] ✅ Fallback authentication AVAILABLE:', {
          method: 'fallback',
          userId: fallbackAuth.user?.id || fallbackAuth.user?._id,
          username: fallbackAuth.user?.username,
          rememberMe: fallbackAuth.rememberMe,
          expiresAt: new Date(fallbackAuth.expiresAt).toISOString(),
          isExpired: Date.now() > fallbackAuth.expiresAt
        });

        // Check if fallback is expired
        if (Date.now() > fallbackAuth.expiresAt) {
          pwaAuthDebug('[PWA Auth] ⚠️ Fallback auth is EXPIRED, clearing...');
          pwaAuthStorage.clearAuth();
          return { success: false, error: 'Fallback auth expired', method: 'none' };
        }

        return {
          success: true,
          user: fallbackAuth.user,
          method: 'fallback',
          expiresAt: fallbackAuth.expiresAt
        };
      } else {
        pwaAuthDebug('[PWA Auth] ❌ Fallback requested but no data available');
      }
    } else {
      pwaAuthDebug('[PWA Auth] ❌ Fallback not available for this scenario:', {
        isStandalone,
        isLocalhost,
        hasFallbackData: !!pwaAuthStorage.getAuth()
      });
    }

    return { success: false, error: error.message, method: 'none' };
  }
};

// Initialize PWA auth monitoring
export const initPWAAuthMonitoring = () => {
  // ✅ FIXED: Always initialize monitoring, regardless of PWA detection
  // Because PWAs often fail detection and cookies are unreliable

  // Check PWA auth every minute
  setInterval(() => {
    const remaining = pwaAuthStorage.getRemainingTime();
    if (remaining < 5 * 60 * 1000) { // Less than 5 minutes
      pwaAuthDebug(`[PWA Auth] Authentication expiring soon: ${Math.floor(remaining / 60000)} minutes`);
    }
  }, 60 * 1000);

  pwaAuthDebug('[PWA Auth] Monitoring initialized');
};

// 🔍 DEBUG: Manual PWA auth state checker (call this in browser console)
export const debugPWAAuthState = () => {
  pwaAuthDebug('🔍 [PWA Auth Debug] Current authentication state:');

  // Check PWA mode
  const isStandalone = window.navigator.standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches;
  pwaAuthDebug('   PWA Mode:', isStandalone);

  // Check environment
  const isLocalhost = window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';
  const isHTTPS = window.location.protocol === 'https:';
  pwaAuthDebug('   Environment:', {
    isLocalhost,
    isHTTPS,
    hostname: window.location.hostname,
    protocol: window.location.protocol
  });

  // Check cookies
  pwaAuthDebug('   Cookies:', {
    hasAccessToken: document.cookie.includes('accessToken='),
    hasRefreshToken: document.cookie.includes('refreshToken='),
    hasFSRemember: document.cookie.includes('FS_REMEMBER='),
    hasDeviceId: document.cookie.includes('deviceId='),
    allCookies: document.cookie || 'No cookies present'
  });

  // Check localStorage fallback
  const fallbackData = localStorage.getItem('pwa_auth_data');
  const fallbackExpires = localStorage.getItem('pwa_auth_expires');
  pwaAuthDebug('   LocalStorage Fallback:', {
    hasData: !!fallbackData,
    hasExpires: !!fallbackExpires,
    expiresValue: fallbackExpires ? new Date(parseInt(fallbackExpires)).toISOString() : null,
    isExpired: fallbackExpires ? Date.now() > parseInt(fallbackExpires) : true
  });

  // Check rememberMe flag
  const rememberMe = localStorage.getItem('fusesoul_remember_me');
  pwaAuthDebug('   Remember Me Flag:', rememberMe);

  // Try to get auth data
  const authData = pwaAuthStorage.getAuth();
  pwaAuthDebug('   PWA Auth Data:', authData ? {
    userId: authData.user?.id || authData.user?._id,
    username: authData.user?.username,
    rememberMe: authData.rememberMe,
    expiresAt: new Date(authData.expiresAt).toISOString(),
    isExpired: Date.now() > authData.expiresAt
  } : null);

  // 🎯 SMART FALLBACK ANALYSIS
  const shouldUseFallback = isStandalone && (isLocalhost || authData);
  pwaAuthDebug('   Smart Fallback Analysis:', {
    shouldUseFallback,
    reason: shouldUseFallback ?
      (isLocalhost ? 'PWA + localhost (cookies unreliable)' : 'PWA + fallback data available') :
      'Not needed (cookies should work)'
  });

  return {
    isPWAMode: isStandalone,
    environment: { isLocalhost, isHTTPS, hostname: window.location.hostname },
    cookies: document.cookie,
    fallbackData: !!fallbackData,
    fallbackExpires: fallbackExpires,
    rememberMe: rememberMe,
    authData: authData,
    smartFallback: shouldUseFallback
  };
};
